import { supabase } from "../lib/supabase";
import { extractFunctionErrorMessage } from "./edgeFunctionErrors";

/**
 * A client for one sales integration OAuth Edge Function (shopify-oauth,
 * amazon-oauth). Every call carries the user's JWT. A failure throws an Error
 * with the function's own message and the HTTP status it answered with
 * (err.status, null when there was no response), so a caller can tell a
 * definitive rejection (confirm's 404 "invalid or expired") from a transient
 * failure (network error, 5xx).
 */
export function createOauthFunctionClient(functionName) {
  async function invoke(action, advertiserId, authToken, extra = {}) {
    if (!supabase) {
      throw new Error("Supabase client is not initialized");
    }
    const { data, error } = await supabase.functions.invoke(functionName, {
      body: { action, advertiser_id: advertiserId, ...extra },
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (error) {
      const err = new Error(await extractFunctionErrorMessage(error));
      // error.context is the raw Response.
      err.status = error?.context?.status ?? null;
      throw err;
    }
    return data;
  }

  /**
   * Invokes the action and returns the `url` it issued, throwing
   * `missingUrlMessage` when there is none. Callers decide whether to navigate.
   */
  async function requestUrl(action, advertiserId, authToken, extra, missingUrlMessage) {
    const data = await invoke(action, advertiserId, authToken, extra);
    if (!data?.url) {
      throw new Error(missingUrlMessage);
    }
    return data.url;
  }

  return { invoke, requestUrl };
}
